import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Badge, Btn, Card, Field, PageHead, SectionHead, Tabs } from '../components/ui/primitives';
import { IncidentsTable } from '../components/IncidentsTable';
import { RELEASE_STATUS_OPTIONS, TESTING_DUTIES, isTesting, statusBadge, type ReleaseStatus } from '../lib/release-status';
import { FrictionPanel, useToast } from '../components/ui/widgets';
import { CountrySelector } from '../components/ui/CountrySelector';
import { apiFetch, useResourceMapped } from '../lib/api';
import { mapPortfolio, type ApiPortfolio } from '../lib/adapt';
import { useMembers } from '../lib/members';
import { useSession } from '../lib/session';
import type { Portfolio, ProductRow, VersionRow } from '../lib/contract';

const USE_FIXTURES = (import.meta.env.VITE_USE_FIXTURES as string | undefined) !== 'false';

type Phase = ProductRow['phase'];

const PHASE_META: Record<Phase, { label: string; kind: 'safe' | 'indet' | 'warn' | 'out' }> = {
  active: { label: 'Active', kind: 'safe' },
  not_released: { label: 'Not released', kind: 'indet' },
  eol: { label: 'EOL', kind: 'warn' },
  retired: { label: 'Retired', kind: 'out' },
};

const PHASE_CONSEQUENCE: Record<Phase, ReactNode> = {
  active:
    'Active — the product is on the market and under full CRA monitoring. A KEV in any Release version triggers a mandatory 24h ENISA obligation.',
  not_released:
    'Not released — no units are placed on the market yet. Findings are tracked, but no Art.14 clock starts until you mark the product Active.',
  eol:
    'EOL — end of life. Support is winding down, but you must still actively notify exploited vulnerabilities for units in the field until the support-end date.',
  retired:
    'Retired — no longer sold or supported. Art.14 monitoring stops; historical incidents stay in the immutable action log.',
};

const CRA_LABEL: Record<ProductRow['craClass'], string> = {
  default: 'Default',
  important_1: 'Important — Class I',
  important_2: 'Important — Class II',
  critical: 'Critical',
};

function initials(name: string): string {
  return name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

export function ProductDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session } = useSession();
  // Poll while any version is still analysing — findings appear the moment the
  // worker finishes, instead of the page being stuck on "analysing…" until a
  // manual reload (the KB scan is inline; the product path is a queued job).
  // fresh=1 bypasses the 60s per-org read cache: the worker (a separate process)
  // can't invalidate the web cache, so without this a completed analysis stays
  // "analysing…" until the TTL. This is the analysis-watching page — always live.
  const portfolio = useResourceMapped<ApiPortfolio, Portfolio>(session, '/api/portfolio?fresh=1', mapPortfolio, {
    pollWhile: (p) => p.products.some((pr) => pr.versions.some((v) => v.sbomStatus === 'pending')),
  });
  const { members } = useMembers(session.organizationId);
  const product = portfolio.data?.products.find((p) => p.id === id);

  // Hooks run unconditionally (stable order) — safe defaults when product is missing.
  const [phase, setPhase] = useState<Phase>(product?.phase ?? 'active');
  const [markets, setMarkets] = useState<string[]>(product?.markets ?? []);
  const [editMarkets, setEditMarkets] = useState(false);
  // Land on the Reports tab when returning from an incident opened here (?tab=reports).
  const [tab, setTab] = useState<'versions' | 'reports'>(searchParams.get('tab') === 'reports' ? 'reports' : 'versions');
  const [panelOpen, setPanelOpen] = useState(false);
  const [targetPhase, setTargetPhase] = useState<Phase>(product?.phase ?? 'active');
  const [toast, showToast] = useToast();
  // Real SBOM upload (file → new version → REAL-TIME inline analysis, like KB scan)
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [verLabel, setVerLabel] = useState('');
  const [uploadStatus, setUploadStatus] = useState<ReleaseStatus>('release'); // only 'release' is Art.14-tracked
  const [uploadStage, setUploadStage] = useState<null | 'preparing' | 'analysing'>(null);
  const [dragActive, setDragActive] = useState(false);
  const uploading = uploadStage !== null;
  // Inline product-info editing (name / description)
  const [editing, setEditing] = useState(false);
  const [eName, setEName] = useState('');
  const [eDesc, setEDesc] = useState('');
  async function uploadSbom(file: File) {
    if (!product) return;
    if (USE_FIXTURES) { showToast(`Demo: ${file.name} queued for analysis.`); return; }
    try {
      setUploadStage('preparing');
      const label = verLabel.trim() || `v${new Date().toISOString().slice(0, 10)}`;
      // REUSE a same-label version (a retry must never mint a duplicate — user
      // report 2026-07-20: eight identical versions from repeated uploads).
      const existing = product.versions.find((v) => v.label === label);
      let versionId = existing?.id;
      let swId: string | undefined;
      if (!versionId) {
        const vRes = await apiFetch(session, '/api/portfolio', {
          method: 'POST',
          body: JSON.stringify({ kind: 'version', productId: product.id, label, releaseStatus: uploadStatus }),
        });
        if (!vRes.ok) throw new Error(`version create failed (${vRes.status})`);
        const created = (await vRes.json()) as { versionId: string; swVersionId?: string };
        versionId = created.versionId;
        swId = created.swVersionId;
      }
      if (!swId) {
        const s = await apiFetch(session, '/api/portfolio', {
          method: 'POST', body: JSON.stringify({ kind: 'version-sw-lookup', versionId }),
        });
        if (!s.ok) throw new Error(`sw lookup failed (${s.status})`);
        swId = ((await s.json()) as { swVersionId: string }).swVersionId;
      }
      const content = await file.text();
      // sync=true → the SAME engine as KB scan runs INLINE and persists to the
      // version; the response returns only when analysis is done, so we can go
      // straight to the results — no queue, no polling, no "analysing…" limbo.
      setUploadStage('analysing');
      const u = await apiFetch(session, '/api/uploads', {
        method: 'POST', body: JSON.stringify({ swVersionId: swId, filename: file.name, content, sync: true }),
      });
      if (!u.ok) throw new Error(`upload failed (${u.status})`);
      setVerLabel('');
      setUploadStage(null);
      showToast(`${file.name} analysed and saved as ${label}.`);
      navigate(`/app/products/${product.id}/analysis/${versionId}`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e));
      setUploadStage(null);
    }
  }

  // Release-status change (only 'release' is Art.14-tracked — CRA FAQ/Recital 37).
  async function setVersionStatus(v: VersionRow, status: ReleaseStatus) {
    const r = await apiFetch(session, '/api/portfolio', {
      method: 'PATCH',
      body: JSON.stringify({ versionId: v.id, patch: { releaseStatus: status } }),
    });
    if (!r.ok) { showToast(`status change failed (${r.status})`); return; }
    showToast(status === 'release'
      ? `${v.label} marked Release — Art.14 clocks apply.`
      : isTesting(status)
        ? `${v.label} marked ${status.toUpperCase()} (testing) — not tracked. Mind the Recital 37 duties.`
        : `${v.label} marked internal — not tracked.`);
    portfolio.reload();
  }

  async function saveInfo() {
    if (!product) return;
    if (!USE_FIXTURES) {
      const r = await apiFetch(session, '/api/portfolio', {
        method: 'PATCH',
        body: JSON.stringify({ productId: product.id, patch: { name: eName.trim() || product.name, description: eDesc.trim() } }),
      });
      if (!r.ok) { showToast(`save failed (${r.status})`); return; }
    }
    showToast('Product updated.');
    setEditing(false);
    if (!USE_FIXTURES) portfolio.reload();
  }

  // Async load (live mode): sync the editable fields once the product arrives.
  useEffect(() => {
    if (product) {
      setPhase(product.phase);
      setMarkets(product.markets);
      setTargetPhase(product.phase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  if (portfolio.loading) {
    return <div className="hint" style={{ padding: 24 }}>Loading product…</div>;
  }
  if (!product) {
    return (
      <>
        <PageHead
          title="Product not found"
          sub={`No product matches “${id ?? ''}”.`}
          crumbs={<><Link to="/app/products">Products</Link> ／ not found</>}
        />
        <div className="hint">
          It may have been removed. <Link to="/app/products">Back to Products →</Link>
        </div>
      </>
    );
  }

  const owner = product.ownerUserRef ? members.find((m) => m.id === product.ownerUserRef) ?? null : null;
  const pm = PHASE_META[phase];
  const marketsLabel = markets.join(' · '); // REVIEW-FIXES C-7 — includes non-EU markets like CH
  const analysisPath = (vid: string) => `/app/products/${product.id}/analysis/${vid}`;

  function openPanel() {
    setTargetPhase(phase);
    setPanelOpen(true);
  }

  async function patchProduct(patch: Record<string, unknown>): Promise<void> {
    if (USE_FIXTURES) return;
    try {
      const r = await apiFetch(session, '/api/portfolio', {
        method: 'PATCH',
        body: JSON.stringify({ productId: product!.id, patch }),
      });
      if (!r.ok) showToast(`Save failed (${r.status})`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e));
    }
  }

  function confirmPhase() {
    setPhase(targetPhase);
    setPanelOpen(false);
    showToast(`Phase changed to ${PHASE_META[targetPhase].label} — logged to the audit trail.`);
    void patchProduct({ phase: targetPhase }); // persist server-side (live mode)
  }

  function toggleMarketsEdit() {
    // Saving happens on "Done" (edit → view transition).
    if (editMarkets) void patchProduct({ markets });
    setEditMarkets((e) => !e);
  }

  // REAL per-version counts (read model) — findings are tracked for EVERY
  // version, release or not; only the Art.14 clock depends on the release flag.
  function findingsBadge(v: VersionRow): ReactNode {
    const n = v.openFindings ?? 0;
    const a = v.art14Open ?? 0;
    if (n === 0 && a === 0) {
      return v.sbomStatus === 'none'
        ? <Badge kind="out">no SBOM yet</Badge>
        : v.sbomStatus === 'pending'
          ? <Badge kind="med">analysing…</Badge>
          : v.sbomStatus === 'failed'
            ? <Badge kind="crit" data-testid="version-failed">analysis failed — re-upload</Badge>
            : <Badge kind="safe">[✓] clean</Badge>;
    }
    return (
      <Badge kind={a > 0 ? 'crit' : 'warn'} data-testid="version-findings">
        {n}{a ? ` · ${a} Art.14` : ''}
      </Badge>
    );
  }

  return (
    <>
      <PageHead
        title={product.name}
        sub={
          <>
            {product.description ? `${product.description} · ` : ''}
            sold in {markets.length ? marketsLabel : 'no markets set'}
          </>
        }
        crumbs={<><Link to="/app/products">Products</Link> ／ {product.name}</>}
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge kind={pm.kind} style={{ padding: '6px 11px' }}>Phase: {pm.label}</Badge>
            <Btn sm onClick={() => (panelOpen ? setPanelOpen(false) : openPanel())}>Change phase…</Btn>
            <Btn sm onClick={() => { setEName(product.name); setEDesc(product.description ?? ''); setEditing((e) => !e); }}>
              {editing ? 'Close edit' : 'Edit product…'}
            </Btn>
          </div>
        }
      />

      {editing && (
        <Card style={{ padding: 18, marginBottom: 14 }}>
          <div className="section-head"><h3>Edit product information</h3></div>
          <div className="form" style={{ marginTop: 8, maxWidth: 640 }}>
            <Field label="Product name" req>
              <input className="input" value={eName} onChange={(e) => setEName(e.target.value)} />
            </Field>
            <Field label="Description">
              <textarea className="input" rows={3} value={eDesc} onChange={(e) => setEDesc(e.target.value)} placeholder="What this product is, for the team and for reports." />
            </Field>
            <div className="form-actions">
              <Btn onClick={() => setEditing(false)}>Cancel</Btn>
              <Btn variant="primary" onClick={() => void saveInfo()}>Save</Btn>
            </div>
          </div>
        </Card>
      )}

      {panelOpen && (
        <Card style={{ padding: 18, marginTop: 14, borderColor: 'color-mix(in srgb, var(--medium) 45%, var(--border))' }}>
          <SectionHead title="Change product phase" aside={<Badge kind="warn">deliberate action · logged</Badge>} />
          <p className="hint" style={{ margin: '10px 0 4px' }}>
            Phase changes alter CRA obligations for this product. This is intentionally a two-step action to prevent accidental changes.
          </p>
          <Field label="New phase">
            <select
              className="select"
              value={targetPhase}
              onChange={(e) => setTargetPhase(e.target.value as Phase)}
              style={{ maxWidth: 460 }}
            >
              <option value="active">Active</option>
              <option value="not_released">Not released</option>
              <option value="eol">EOL — end of life</option>
              <option value="retired">Retired</option>
            </select>
          </Field>
          <FrictionPanel
            key={targetPhase}
            title="What this means"
            consequence={PHASE_CONSEQUENCE[targetPhase]}
            tone="medium"
            ackLabel={`I understand this changes CRA obligations for ${product.name} and will be recorded in the audit log.`}
            confirmLabel="Confirm phase change"
            onConfirm={confirmPhase}
            onCancel={() => setPanelOpen(false)}
          />
        </Card>
      )}

      <Card style={{ padding: 18, marginTop: 16 }}>
        <SectionHead title="Details" />
        <dl className="kv" style={{ marginTop: 8 }}>
          <dt>Owner</dt>
          <dd>
            {owner
              ? <span className="owner"><span className="ava-sm">{initials(owner.name)}</span>{owner.name}</span>
              : <span className="hint">unassigned</span>}
          </dd>
          <dt>CRA class</dt>
          <dd>{CRA_LABEL[product.craClass]}</dd>
          <dt>Phase</dt>
          <dd><Badge kind={pm.kind}>{pm.label}</Badge></dd>
          <dt>Markets</dt>
          <dd className="mono">{markets.length ? marketsLabel : '—'}</dd>
        </dl>
      </Card>

      <div style={{ marginTop: 18 }}>
        <Tabs
          tabs={[
            { key: 'versions', label: 'Versions / SBOMs' },
            { key: 'reports', label: 'Reports' },
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === 'versions' && (
          <div className="tabpane on">
            <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="hint">SBOMs for {product.name} — upload here, or let CI / GitHub push new versions via the API.</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="input" style={{ width: 130 }} placeholder="version label" value={verLabel} onChange={(e) => setVerLabel(e.target.value)} />
                <select className="select" style={{ width: 230 }} value={uploadStatus} data-testid="upload-release-status"
                  onChange={(e) => setUploadStatus(e.target.value as ReleaseStatus)}>
                  {RELEASE_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <Btn disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? (uploadStage === 'preparing' ? 'Preparing…' : 'Analysing…') : 'Upload SBOM'}</Btn>
              </div>
            </div>
            {isTesting(uploadStatus) && (
              <p className="hint" data-testid="testing-duties" style={{ margin: '8px 0 0', color: 'var(--medium)' }}>
                ⚠ {TESTING_DUTIES}
              </p>
            )}
            {uploadStatus === 'internal' && (
              <p className="hint" style={{ margin: '8px 0 0' }}>
                Internal builds are analysed but NOT tracked for Art.14 — no clocks, no alerts.
              </p>
            )}
            <input ref={fileRef} type="file" accept=".json,.xml,.spdx" style={{ display: 'none' }}
              onChange={(e) => { const fl = e.target.files?.[0]; if (fl) void uploadSbom(fl); e.target.value = ''; }} />
            <div className={`dropzone${dragActive ? ' dragover' : ''}`} data-testid="dropzone"
              onClick={() => { if (!uploading) fileRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
              onDragEnter={(e) => { e.preventDefault(); if (!uploading) setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={(e) => { e.preventDefault(); setDragActive(false); if (uploading) return; const fl = e.dataTransfer.files?.[0]; if (fl) void uploadSbom(fl); }}>
              {uploading ? (
                <div className="big" data-testid="upload-progress">
                  <span className="spinner" aria-hidden="true" />
                  {uploadStage === 'preparing' ? 'Preparing version…' : `Analysing ${product.name} in real time…`}
                </div>
              ) : (
                <div className="big">{dragActive ? '⬇ Drop the SBOM to upload' : `Upload or drop here a new SBOM for ${product.name}`}</div>
              )}
              {uploading ? 'Running the same analysis as Knowledge Base — this may take a few seconds' : 'CycloneDX or SPDX JSON · drop it here or click · CI / GitHub can push via the API'}
            </div>
            <div className="tablewrap" style={{ marginTop: 14 }}>
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Phase</th>
                    <th>SBOM quality</th>
                    <th>Findings</th>
                    <th className="r">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {product.versions.map((v) => (
                    <tr key={v.id} className="clk" onClick={() => navigate(analysisPath(v.id))}>
                      <td className="mono">{v.label}</td>
                      <td>
                        {(() => { const b = statusBadge(v.releaseStatus ?? (v.release ? 'release' : 'internal')); return <Badge kind={b.kind}>{b.text}</Badge>; })()}
                        {!USE_FIXTURES && (
                          <span onClick={(e) => e.stopPropagation()} style={{ marginLeft: 8 }}>
                            <select className="select" style={{ width: 200, padding: '4px 8px' }} data-testid="row-release-status" data-version={v.id}
                              value={v.releaseStatus ?? (v.release ? 'release' : 'internal')}
                              onChange={(e) => void setVersionStatus(v, e.target.value as ReleaseStatus)}>
                              {RELEASE_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </span>
                        )}
                      </td>
                      <td className="mono">{v.sbomQuality != null ? `${v.sbomQuality}/100` : '—'}</td>
                      <td>{findingsBadge(v)}</td>
                      <td className="r">
                        <Link to={analysisPath(v.id)} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--teal)' }}>
                          View analysis →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === 'reports' && (
          <div className="tabpane on">
            <p className="hint" style={{ marginBottom: 12 }}>
              Every Art.14 event and manual case for <b>{product.name}</b>. Select rows to edit them together, or use the Archived filter to see removed incidents.
            </p>
            <IncidentsTable productName={product.name} />
          </div>
        )}
      </div>

      <Card style={{ padding: 18, marginTop: 18 }}>
        <SectionHead
          title="Markets"
          aside={<Btn sm onClick={toggleMarketsEdit}>{editMarkets ? 'Done' : 'Edit markets'}</Btn>}
        />
        <p className="hint" style={{ margin: '6px 0 12px' }}>
          Commercial markets where {product.name} is placed on the market. Includes non-EU markets (CH); the EU-only Art.14 scope is derived from this automatically.
        </p>
        <CountrySelector selected={markets} editable={editMarkets} onChange={setMarkets} />
      </Card>

      {toast}
    </>
  );
}
